import { useEffect, useState } from 'react';
import { docHasText, docsEqual, type AdvisorDoc, type AdvisorVersionMeta } from '@agio/core/domain';
import { Card } from '../../Card.js';
import { ErrorBanner, Spinner } from '../../FormBits.js';
import { AdvisorEditor } from '../../advisor/AdvisorEditor.js';
import { advisor } from './advisorApi.js';
import { VersionsList } from './VersionsList.js';

/**
 * The main summary. The editor opens on the latest version, so writing the
 * next one starts from the last; saving never overwrites — it adds a version.
 */
export function MainSummaryTab({ tick, onSaved }: { tick: number; onSaved: () => void }) {
  const [versions, setVersions] = useState<AdvisorVersionMeta[] | null>(null);
  const [base, setBase] = useState<AdvisorDoc | null>(null);
  const [doc, setDoc] = useState<AdvisorDoc | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    (async () => {
      const list = await advisor.versions();
      const latest = list[0] ? (await advisor.version(list[0].version)).body : null;
      if (!live) return;
      setVersions(list);
      setBase(latest);
      setDoc(latest);
    })().catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [tick]);

  const latest = versions?.[0] ?? null;
  const unchanged = doc === null || docsEqual(doc, base);
  const save = async () => {
    if (!doc) return;
    setBusy(true);
    setError(null);
    try {
      const v = await advisor.saveVersion(doc);
      setSavedNote(`נשמרה גרסה ${v.version}`);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-5">
      <Card>
        <div className="grid grid-cols-1 gap-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">הסקירה הראשית</h2>
            <p className="text-sm text-muted-foreground">
              {latest ? `נפתחה מגרסה ${latest.version}. שמירה יוצרת גרסה חדשה — הגרסאות הקודמות לא משתנות.` : 'עדיין אין גרסה שמורה.'}
            </p>
          </div>
          <p className="text-sm text-muted-foreground">
            שמירה כאן לא משנה את מה שהלקוחות רואים. כדי שלקוח יראה גרסה, מעתיקים אותה אליו ומפרסמים במסך "סקירת יועץ" שתחת "עדכון לקוח".
          </p>
          {versions !== null && <AdvisorEditor value={doc} onChange={(d) => { setDoc(d); setSavedNote(null); }} label="הסקירה הראשית" />}
          <ErrorBanner error={error} title="השמירה נכשלה" />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={busy || unchanged || !docHasText(doc)}
              className="flex items-center gap-2 rounded-md bg-primary px-5 py-2 font-semibold text-primary-foreground disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy && <Spinner />}
              שמירה כגרסה חדשה
            </button>
            {unchanged && latest && <span className="text-sm text-muted-foreground">אין שינויים מגרסה {latest.version}</span>}
            {savedNote && <span role="status" className="text-sm font-semibold text-success">{savedNote}</span>}
          </div>
        </div>
      </Card>
      <VersionsList versions={versions} />
    </div>
  );
}
